import { useState, useEffect } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { Menu, X, ChevronDown, Calendar, Phone, Mail, MapPin } from 'lucide-react'

export default function Header() {
  const [isOpen, setIsOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [showBehandlingerDropdown, setShowBehandlingerDropdown] = useState(false)
  const location = useLocation()

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false)
    setShowBehandlingerDropdown(false)
  }, [location])

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true)
      } else {
        setIsScrolled(false)
      }
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navItems = [
    { name: 'Forside', path: '/' },
    { name: 'Om os', path: '/om-os' },
    { name: 'Klinikken', path: '/klinikken' },
    {
      name: 'Behandlinger',
      path: '/behandlinger',
      dropdown: [
        { name: 'Alle Behandlinger', path: '/behandlinger' },
        { name: 'Rørbæk Teknikken', path: '/rorbaek-teknikken' },
        { name: 'Muskelterapi', path: '/behandlinger/muskelterapi' },
        { name: 'Spædbørn', path: '/behandlinger/spaedborn' },
        { name: 'Cupping', path: '/behandlinger/cupping' },
        { name: 'Fysiurgisk Massage', path: '/behandlinger/fysiurgisk-massage' },
        { name: 'Hovedpine', path: '/behandlinger/hovedpine' },
        { name: 'Kranio-sakral terapi', path: '/behandlinger/kranio-sakral-terapi' },
        { name: 'Kranio-sakral terapi – børn og voksne', path: '/behandlinger/kranio-sakral-terapi-boern-og-voksne' },
        { name: 'Myofascial Release (MFR)', path: '/behandlinger/myofascial-release' },
        { name: 'Øreakupunktur (NADA)', path: '/behandlinger/oereakupunktur-nada' },
        { name: 'Organmassage', path: '/behandlinger/organmassage' },
        { name: 'Ilt-terapi', path: '/behandlinger/ilt-terapi' },
        { name: 'Vejrtrækning', path: '/behandlinger/vejrtraekning' },
        { name: 'Bækkenløsning', path: '/behandlinger/baekkenloesning' },
        { name: 'Kolik', path: '/behandlinger/kolik' },
        { name: 'Infrarød terapi', path: '/behandlinger/infraroed-terapi' },
        { name: 'Akupunktur', path: '/behandlinger/akupunktur' },
      ]
    },
    { name: 'Videre forløb', path: '/videre-forloeb' },
    { name: 'Gode erfaringer', path: '/gode-erfaringer' },
    { name: 'Spørgsmål & Svar', path: '/faq' },
    { name: 'Kontakt', path: '/kontakt' },
  ]

  return (
    <>
      {/* Top Info Bar - Desktop Only */}
      <div className="hidden min-[1200px]:block bg-[#1E352F] text-[#F5F0E6] text-xs py-2 px-6 border-b border-[#2D4A43]">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-[#eebb53]" />
              Gravene 28B, 8800 Viborg
            </span>
            <span className="flex items-center gap-1.5">
              <Phone className="w-3.5 h-3.5 text-[#eebb53]" />
              Tlf: <a href="tel:86639393" className="hover:underline">86 63 93 93</a>
            </span>
            <span className="flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5 text-[#eebb53]" />
              <a href="mailto:im@muskelterapi.com" className="hover:underline">im@muskelterapi.com</a>
            </span>
          </div>
          <div>
            <span>CVR: 32389260</span>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-[#FDFBF7]/95 backdrop-blur-md shadow-sm border-b border-[#E6DFD3] py-3'
            : 'bg-[#FDFBF7] border-b border-[#F5F0E6] py-5'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Desktop Layout (>= 1200px) */}
          <div className="hidden min-[1200px]:block">
            {/* Beautiful 2-Row Layout */}
            <div className="flex flex-col items-center gap-4 transition-all duration-300 animate-in fade-in duration-300">
              {/* Row 1: Only Logo */}
              <div className="flex justify-center py-2">
                <Link to="/" className="flex items-center group">
                  <img 
                    src="https://assets.auxodev.dk/assets/bd5a76fd-d82e-431d-9e97-5b07d543e9b6/16078238-fbcc-4d39-8388-5a0f35514b2c.png" 
                    alt="Rørbækklinikken" 
                    className="h-24 w-auto object-contain transition-transform group-hover:scale-105"
                  />
                </Link>
              </div>

              {/* Row 2: Navigation and CTA */}
              <div className="w-full flex justify-between items-center border-t border-[#F5F0E6] pt-3">
                <nav className="flex items-center gap-1 justify-start flex-1">
                  {navItems.map((item) => {
                    if (item.dropdown) {
                      return (
                        <div
                          key={item.name}
                          className="relative"
                          onMouseEnter={() => setShowBehandlingerDropdown(true)}
                          onMouseLeave={() => setShowBehandlingerDropdown(false)}
                        >
                          <button
                            className={`px-3.5 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-1 ${
                              location.pathname.startsWith('/behandlinger')
                                ? 'text-[#eebb53] font-semibold'
                                : 'text-[#1E352F]/80 hover:text-[#1E352F] hover:bg-[#F5F0E6]/50'
                            }`}
                          >
                            {item.name}
                            <ChevronDown className="w-4 h-4" />
                          </button>

                          {/* Dropdown Menu */}
                          {showBehandlingerDropdown && (
                            <div className="absolute left-0 mt-0 w-64 max-h-[380px] overflow-y-auto rounded-lg bg-[#FDFBF7] shadow-lg border border-[#E6DFD3] py-2 z-50 animate-in fade-in slide-in-from-top-1 duration-200">
                              {item.dropdown.map((subItem) => (
                                <NavLink
                                  key={subItem.name}
                                  to={subItem.path}
                                  end={subItem.path === '/behandlinger'}
                                  className={({ isActive }) =>
                                    `block px-4 py-2.5 text-sm transition-colors ${
                                      isActive
                                        ? 'bg-[#F5F0E6] text-[#eebb53] font-semibold'
                                        : 'text-[#1E352F]/80 hover:text-[#1E352F] hover:bg-[#F5F0E6]/30'
                                    }`
                                  }
                                >
                                  {subItem.name}
                                </NavLink>
                              ))}
                            </div>
                          )}
                        </div>
                      )
                    }

                    return (
                      <NavLink
                        key={item.name}
                        to={item.path}
                        className={({ isActive }) =>
                          `px-3.5 py-2 rounded-md text-sm font-medium transition-colors ${
                            isActive
                              ? 'text-[#eebb53] font-semibold'
                              : 'text-[#1E352F]/80 hover:text-[#1E352F] hover:bg-[#F5F0E6]/50'
                          }`
                        }
                      >
                        {item.name}
                      </NavLink>
                    )
                  })}
                </nav>

                <div className="w-[180px] flex justify-end">
                  <Link
                    to="/bestil-tid"
                    className="bg-[#eebb53] hover:bg-[#dca63d] text-white px-5 py-2.5 rounded-full text-sm font-medium transition-all shadow-sm hover:shadow flex items-center gap-2 whitespace-nowrap"
                  >
                    <Calendar className="w-4 h-4" />
                    Book tid online
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Layout (< 1200px) */}
          <div className="flex min-[1200px]:hidden justify-between items-center">
            {/* Logo */}
            <Link to="/" className="flex items-center group">
              <img 
                src="https://assets.auxodev.dk/assets/bd5a76fd-d82e-431d-9e97-5b07d543e9b6/16078238-fbcc-4d39-8388-5a0f35514b2c.png" 
                alt="Rørbækklinikken" 
                className="h-16 w-auto object-contain transition-transform group-hover:scale-105"
              />
            </Link>

            {/* Mobile Menu Button & CTA */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="p-2 rounded-md text-[#1E352F] hover:bg-[#F5F0E6] transition-colors"
                aria-label="Toggle menu"
              >
                {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Dropdown Menu (< 1200px) */}
        {isOpen && (
          <div className="min-[1200px]:hidden bg-[#FDFBF7] border-t border-[#E6DFD3] py-4 px-4 space-y-1 shadow-inner max-h-[calc(100vh-80px)] overflow-y-auto">
            {navItems.map((item) => {
              if (item.dropdown) {
                return (
                  <div key={item.name} className="space-y-1 py-1">
                    <div className="px-3 text-xs font-semibold text-[#eebb53] uppercase tracking-wider">
                      {item.name}
                    </div>
                    {item.dropdown.map((subItem) => (
                      <NavLink
                        key={subItem.name}
                        to={subItem.path}
                        end={subItem.path === '/behandlinger'}
                        className={({ isActive }) =>
                          `block px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                            isActive
                              ? 'bg-[#F5F0E6] text-[#eebb53]'
                              : 'text-[#1E352F]/80 hover:text-[#1E352F] hover:bg-[#F5F0E6]/50'
                          }`
                        }
                      >
                        {subItem.name}
                      </NavLink>
                    ))}
                  </div>
                )
              }

              return (
                <NavLink
                  key={item.name}
                  to={item.path}
                  className={({ isActive }) =>
                    `block px-3 py-2 rounded-md text-base font-medium transition-colors ${
                      isActive
                        ? 'bg-[#F5F0E6] text-[#eebb53]'
                        : 'text-[#1E352F]/80 hover:text-[#1E352F] hover:bg-[#F5F0E6]/50'
                    }`
                  }
                >
                  {item.name}
                </NavLink>
              )
            })}

            {/* Mobile Contact Info inside menu */}
            <div className="pt-6 mt-4 border-t border-[#E6DFD3] px-3 space-y-3 text-sm text-[#1E352F]/70">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#eebb53]" />
                Gravene 28B, 8800 Viborg
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-[#eebb53]" />
                <a href="tel:86639393" className="hover:underline">86 63 93 93</a>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#eebb53]" />
                <a href="mailto:im@muskelterapi.com" className="hover:underline">im@muskelterapi.com</a>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  )
}
